import React from 'react';
import SparkOverlay from 'sharedComponents/UI/SparkOverlay';
import BoardNodeFooter from './BoardNodeFooter';
import BoardNodeHeader from './BoardNodeHeader';
import ChatMessageBubble, { FooterActions } from '../Chats/ChatMessageBubble';
import SparkChatBubbleInner from '../Chats/SparkChatBubbleInner';
import { Box, Grid } from '@mui/material';
import { FileRejection } from 'react-dropzone';
import { useAddChatMessage } from 'hooks/chats/useAddChatMessage';
import { useBlobStorage } from 'hooks/useBlobStorage';
import { useBoardsByID } from 'hooks/Board';
import { SparkScrollableGridContainer } from 'sharedComponents/UI';
import type { BoardNodes } from 'types/BoardNode';
import type { BoardNodeType } from 'types/entityTypes/boardNodeTypes';
import { useBoardNodeTypes } from 'hooks/EntityTypes/useBoardNodeTypes';
import { useUpdateChatMessage } from 'hooks/chats/useUpdateChatMessage';
import { useReactFlowHelpers } from './helper/useReactFlowHelpers';
import { AttachmentCardType } from '../Chats/AttachmentCard';
import { useInfiniteChatMessages } from 'hooks/chats/useInfiniteChatMessages';
import ChatInfiniteScroll from 'sharedComponents/UI/ChatInfiniteScroll';
import { useWorkspaceStore } from 'stores';
import { v4 } from 'uuid';
import type { ListOptionsType } from 'types/common/DropdownOption';
const MemoizedChatMessage = React.memo(ChatMessageBubble);

type BoardNodeExpandedViewProps = {
  onClose: () => void;
  boardNode: BoardNodes;
  open: boolean;
  boardNodeTitle: BoardNodes['attributes']['title'];
  nodeType?: BoardNodeType | undefined;
  children: React.ReactNode;
  boardID: string;
  onTitleSave: (newValue: string) => void;
  boardNodeOptions: ListOptionsType[];
};

function BoardNodeExpandedView({
  open,
  onClose,
  boardNode,
  boardNodeTitle,
  nodeType,
  children,
  boardID,
  onTitleSave,
  boardNodeOptions,
}: BoardNodeExpandedViewProps) {
  const { boardChat } = useBoardsByID(boardID);
  const {
    chatMessages,
    isLoading: isLoadingMoreChats,
    hasNextPage,
    loadMoreMessages,
    isFetchingNextPage,
  } = useInfiniteChatMessages(boardChat?.attributes.chatID);

  const { mutateAsync: updateChatMessage } = useUpdateChatMessage(
    undefined,
    boardChat?.attributes.chatID
  );
  const { workspace } = useWorkspaceStore();
  const { handleBoardEvent } = useReactFlowHelpers();
  const { mutateAsync: addMessage } = useAddChatMessage(
    boardChat?.attributes.chatID,
    'board',
    handleBoardEvent
  );
  const { idMap } = useBoardNodeTypes();
  const { handleFileUpload } = useBlobStorage(
    workspace?.attributes.containerName || 'app-assets'
  );
  const [attachments, setAttachments] = React.useState<AttachmentCardType[]>(
    []
  );
  React.useEffect(() => {
    if (!idMap) {
      return;
    }

    setAttachments((prev) => {
      if (
        prev.find(
          (attachment) =>
            attachment.cardType === 'boardNode' &&
            attachment.id === boardNode.id
        )
      ) {
        return prev;
      }
      return [
        ...prev,
        {
          id: boardNode?.id,
          title: boardNode?.attributes.title || '',
          cardType: 'boardNode',
          fileTypeTag: '',
          thumbnail:
            idMap[boardNode.attributes.boardNodeTypeID].attributes.iconURL ||
            '',
          isUploading: false,
          details: 'Card',
          file: null,
          actionKey: boardNode?.id,
        },
      ];
    });
  }, [
    boardNode.attributes.boardNodeTypeID,
    boardNode.attributes.title,
    boardNode?.id,
    idMap,
  ]);

  const handleLoadOlderMessages = React.useCallback(() => {
    if (!hasNextPage || isFetchingNextPage) {
      return;
    }

    loadMoreMessages && loadMoreMessages();
  }, [loadMoreMessages, hasNextPage, isFetchingNextPage]);

  const handleAddAttachments = (attachments: AttachmentCardType) => {
    setAttachments((prev) => {
      // Check if attachment with same id already exists
      const exists = prev.some(
        (attachment) => attachment.id === attachments.id
      );
      if (exists) return prev;
      return [...prev, attachments];
    });
  };

  const handleMessageSend = async (message: string) => {
    const chatMessageID = v4();
    await addMessage({
      id: chatMessageID,
      content: message,
      boardID,
      items: attachments.map((item) => ({
        type: item.cardType,
        id: item.id,
      })),
    });
  };

  const handleSuccessfulUpload = async (
    acceptedFiles: File[],
    fileRejections: FileRejection[]
  ) => {
    const result = await handleFileUpload(acceptedFiles, fileRejections);

    const fileAttachments: AttachmentCardType[] =
      result?.map((item) => {
        const fileExtension =
          item.file.name.split('.').pop()?.toLowerCase() || '';
        return {
          id: item.file.name, // Using temporary ID based on filename
          title: item.file.name,
          fileTypeTag: fileExtension,
          file: item.file,
          details: 'Document',
          isUploading: true,
          cardType: 'file',
          thumbnail: '',
          actionKey: item.file.name,
        };
      }) || [];
    setAttachments(fileAttachments);

    const URLs = result?.map((upload) => upload.url);
    return URLs;
  };

  const handleAttachmentRemove = (
    cardType: AttachmentCardType['cardType'],
    id: AttachmentCardType['id']
  ) => {
    if (cardType === 'boardNode') {
      // handleRemoveNodesFromSelection(actionKey);
    } else {
      setAttachments((prev) =>
        prev.filter((attachment) => attachment.id !== id)
      );
      // handleDelete(id);
    }
  };

  const handleActionClick = (
    type: FooterActions,
    messageID: string,
    secondaryID?: string
  ) => {
    updateChatMessage({
      messageID,
      chatMessageRatingTypeID: Number(secondaryID),
    });
    switch (type) {
      case 'like':
        updateChatMessage({
          messageID,
          chatMessageRatingTypeID: Number(secondaryID),
        });
        break;
      case 'dislike':
        updateChatMessage({
          messageID,
          chatMessageRatingTypeID: Number(secondaryID),
        });
        break;
      case 'duplicate':
        break;
    }
  };

  const attachmentBubbles = attachments.filter(
    (attachment) => attachment.cardType !== 'boardNode'
  );

  return (
    <SparkOverlay
      onClose={onClose}
      open={open}
      childrenContainerStyles={{
        overflow: 'hidden',
      }}
      render={({ isExpanded }) => (
        <Grid container item flex={1} gap={1} height='100%'>
          <SparkScrollableGridContainer
            paddingBottom={2}
            scrollableContainerStyle={{ padding: '0' }}
            actionContainerStyles={{ padding: 0 }}
            maxWidth={isExpanded ? '30rem' : '100%'}
            actions={
              <SparkChatBubbleInner
                attachments={attachmentBubbles}
                onSendMessage={handleMessageSend}
                onFileUpload={handleSuccessfulUpload}
                onAttachmentRemove={handleAttachmentRemove}
                expandable={false}
                onAddAttachments={handleAddAttachments}
                chatID={boardChat?.attributes.chatID}
              />
            }
          >
            <ChatInfiniteScroll
              isLoading={isLoadingMoreChats}
              isFetchingNextPage={isFetchingNextPage}
              onLoadOlderMessages={handleLoadOlderMessages}
            >
              {chatMessages?.map((message, index, messages) => (
                <MemoizedChatMessage
                  id={message.id}
                  key={message.id}
                  message={message.attributes.content}
                  isUserMessage={message.attributes.authorRoleID === 1}
                  isStreaming={message.attributes.isStreaming || false}
                  isLastMessage={messages.length === index + 1}
                  error={message.attributes.error}
                  onActionClick={handleActionClick}
                  layout='one-column'
                  chatMessageRatingTypeID={
                    message.attributes.chatMessageRatingTypeID
                  }
                  isExpandedViewOpen={true}
                  PluginsUsed={message.attributes?.functionCalls || []}
                  pluginsPill={message.attributes.pluginsPill}
                  boardWorkingPill={message.attributes.boardWorkingPill}
                />
              ))}
            </ChatInfiniteScroll>
          </SparkScrollableGridContainer>
          {isExpanded && (
            <Grid item flex={1} paddingX={1}>
              {boardNodeOptions?.length > 0 && (
                <Box
                  bgcolor={'white'}
                  display='flex'
                  flexWrap='nowrap'
                  px={1.5}
                  gap={1}
                  borderRadius={'40px'}
                  sx={{ border: '2px solid #eeeeee' }}
                  alignItems='center'
                  justifyContent='center'
                  width='auto'
                  position='absolute'
                  top={20}
                  left='46%'
                  zIndex={100}
                >
                  {boardNodeOptions.map((option, index) => (
                    <Box
                      key={index}
                      sx={{ display: 'flex', alignItems: 'center' }}
                    >
                      {option.icon}
                    </Box>
                  ))}
                </Box>
              )}
              <Grid
                container
                border={(theme) => `1px solid ${theme.palette.borderColor}`}
                borderRadius='10px'
                sx={{ cursor: 'default' }}
                overflow='hidden'
                component='div'
                bgcolor='white'
              >
                <BoardNodeHeader
                  title={boardNodeTitle || ''}
                  onTitleSave={onTitleSave}
                  isReadOnlyMode={false}
                />

                <Grid
                  item
                  container
                  maxHeight='82vh'
                  overflow='scroll'
                  justifyContent={'center'}
                >
                  {children}
                </Grid>
                <BoardNodeFooter suggestions={[]} />
              </Grid>
            </Grid>
          )}
        </Grid>
      )}
    />
  );
}

export default BoardNodeExpandedView;
